import React, { useMemo, useRef, useState } from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";

const Section = styled.section`
  ${tw`w-full`}
  background: #fffbdc;
  padding: clamp(3rem, 2.2rem + 2vw, 4.5rem) 1.5rem clamp(4rem, 2.6rem + 3vw, 6rem);
`;

const Inner = tw.div`max-w-5xl mx-auto`;

const IntroBlock = tw.div`text-center max-w-3xl mx-auto`;
const Title = tw.h2`text-3xl sm:text-4xl font-semibold tracking-tight text-[#14133B]`;
const IntroSubtext = tw.p`mt-3 text-sm sm:text-base text-[#14133B] leading-relaxed`;

const Form = styled.form`
  ${tw`mt-10 grid gap-8`}
  position: relative;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FullWidth = styled.div`
  width: 100%;
  @media (min-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  ${tw`text-sm font-semibold uppercase tracking-[0.18em] text-[#14133B]`}
  display: block;
`;

const baseControlStyles = ({ $hasError }) => css`
  background: #fffbdc;
  border: none;
  border-bottom: 2px solid ${$hasError ? "#E11D48" : "rgba(20, 19, 59, 0.3)"};
  padding: 10px 4px;
  font-size: 1rem;
  color: #14133b;
  transition: border-color 0.2s ease, transform 0.2s ease;
  border-radius: 0;

  &::placeholder {
    color: rgba(20, 19, 59, 0.7);
  }

  &:focus {
    outline: none;
    border-bottom-color: ${$hasError ? "#F43F5E" : "#14133b"};
    transform: translateY(-1px);
  }
`;

const Input = styled.input`
  ${baseControlStyles}
`;

const Textarea = styled.textarea`
  background: #fffbdc;
  border: 2px solid rgba(20, 19, 59, 0.3);
  padding: 16px 14px;
  font-size: 1rem;
  color: #14133b;
  border-radius: 18px;
  transition: border-color 0.2s ease, transform 0.2s ease;
  min-height: 140px;
  resize: vertical;
  width: 100%;

  &::placeholder {
    color: rgba(20, 19, 59, 0.7);
  }

  &:focus {
    outline: none;
    border-color: #14133b;
    transform: translateY(-1px);
  }
`;

const ErrorText = tw.span`text-xs font-medium text-[#E11D48] mt-1`;

const AgreementRow = styled.label`
  ${tw`flex items-start gap-3 text-sm sm:text-base text-[#14133B] leading-relaxed`}
  cursor: pointer;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 3px;
  border-radius: 4px;
  border: 2px solid #14133b;
  background: #fffbdc;
  cursor: pointer;
  position: relative;
  transition: background 0.18s ease;

  &:checked {
    background: #14133b;
  }

  &:checked::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 0px;
    width: 6px;
    height: 12px;
    border: solid #fffbdc;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  &:focus-visible {
    outline: 2px solid rgba(20, 19, 59, 0.6);
    outline-offset: 2px;
  }
`;

const SubmitRow = tw.div`mt-4 flex justify-center`;
const SubmitButton = styled.button`
  ${tw`inline-flex items-center justify-center uppercase font-semibold`}
  padding: 0.9rem 3rem;
  border-radius: 999px;
  background: #14133b !important;
  color: #fffbdc;
  letter-spacing: 0.12em;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 28px rgba(20, 19, 65, 0.18);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const RequiredNote = tw.p`text-sm text-center text-[#14133B]`;
const SubmitMessage = tw.p`mt-4 text-sm text-center`;

const initialValues = {
  memberId: "",
  lastName: "",
  firstName: "",
  email: "",
  mobile: "",
  officeDetails: "",
};

const requiredFields = ["lastName", "firstName", "email", "mobile"];

const fieldPlaceholders = {
  memberId: "Your Grain Member ID (optional)",
  lastName: "Enter last name",
  firstName: "Enter first name",
  email: "name@example.com",
  mobile: "Your mobile number",
  officeDetails:
    "Contact person's email and/or phone number, company's name (optional)",
};

const RecommendOfficeForm = React.forwardRef((_, ref) => {
  const [values, setValues] = useState(initialValues);
  const [agreement, setAgreement] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const fieldRefs = useRef({});

  const fieldDefinitions = useMemo(
    () => [
      { name: "memberId", label: "Member ID", type: "text" },
      { name: "lastName", label: "Last Name*", type: "text" },
      { name: "firstName", label: "First Name*", type: "text" },
      { name: "email", label: "Email Address*", type: "email" },
      { name: "mobile", label: "Mobile Number*", type: "tel" },
    ],
    []
  );

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!values[field]?.toString().trim()) {
        newErrors[field] = "This field is required.";
      }
    });

    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (values.mobile && values.mobile.replace(/\D/g, "").length < 6) {
      newErrors.mobile = "Please enter a valid mobile number.";
    }

    if (!agreement) {
      newErrors.agreement = "Please tick the agreement to continue.";
    }

    return newErrors;
  };

  const scrollToField = (fieldName) => {
    const node = fieldRefs.current[fieldName];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.querySelector("input, textarea")?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage(null);
    setSubmitError(null);

    const validationErrors = validate();

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      scrollToField(Object.keys(validationErrors)[0]);
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch("/api/SendRecommendOffice/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...values, agreement }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = data?.message || "Unable to submit recommendation.";
        const serverErrors = data?.errors || {};
        setSubmitError(message);
        setErrors((prev) => ({ ...prev, ...serverErrors }));
        const firstServerError = Object.keys(serverErrors)[0];
        if (firstServerError) {
          scrollToField(firstServerError);
        }
        return;
      }

      setSubmitMessage(
        "Thanks! Your recommendation has been sent. We'll be in touch shortly."
      );
      setValues(initialValues);
      setAgreement(false);
      setErrors({});
    } catch (error) {
      setSubmitError("Submission failed. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section ref={ref} id="recommend-office-form">
      <Inner>
        <IntroBlock>
          <Title>Recommend Your Office</Title>
          <IntroSubtext>
            Share your details below — we&apos;ll reach out to your office and send a free lunch your way once they come onboard.
          </IntroSubtext>
        </IntroBlock>

        <Form
          ref={(node) => {
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          onSubmit={handleSubmit}
          noValidate
        >
          <RequiredNote>*Required field</RequiredNote>

          <Grid>
            {fieldDefinitions.map(({ name, label, type }) => (
              <Field key={name} ref={(node) => (fieldRefs.current[name] = node)}>
                <Label htmlFor={name}>{label}</Label>
                <Input
                  id={name}
                  name={name}
                  type={type}
                  value={values[name]}
                  placeholder={fieldPlaceholders[name] ?? ""}
                  onChange={(event) => handleChange(name, event.target.value)}
                  $hasError={Boolean(errors[name])}
                />
                {errors[name] && <ErrorText>{errors[name]}</ErrorText>}
              </Field>
            ))}
          </Grid>

          <FullWidth ref={(node) => (fieldRefs.current.officeDetails = node)}>
            <Label htmlFor="officeDetails">Office Details</Label>
            <Textarea
              id="officeDetails"
              name="officeDetails"
              value={values.officeDetails}
              placeholder={fieldPlaceholders.officeDetails}
              onChange={(event) => handleChange("officeDetails", event.target.value)}
              $hasError={Boolean(errors.officeDetails)}
            />
            {errors.officeDetails && <ErrorText>{errors.officeDetails}</ErrorText>}
          </FullWidth>

          <FullWidth ref={(node) => (fieldRefs.current.agreement = node)}>
            <AgreementRow>
              <Checkbox
                checked={agreement}
                onChange={(event) => {
                  setAgreement(event.target.checked);
                  if (errors.agreement && event.target.checked) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.agreement;
                      return next;
                    });
                  }
                }}
              />
              <span>
                I agree to be contacted by Grain about this office recommendation
                and consent to my details being used to follow up on this referral.
              </span>
            </AgreementRow>
            {errors.agreement && <ErrorText>{errors.agreement}</ErrorText>}
          </FullWidth>

          <SubmitRow>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting…" : "Submit Recommendation"}
            </SubmitButton>
          </SubmitRow>

          {submitMessage && <SubmitMessage css={[tw`text-[#2F855A]`]}>{submitMessage}</SubmitMessage>}
          {submitError && <SubmitMessage css={[tw`text-[#E53E3E]`]}>{submitError}</SubmitMessage>}
        </Form>
      </Inner>
    </Section>
  );
});

RecommendOfficeForm.displayName = "RecommendOfficeForm";

export default RecommendOfficeForm;
